import React, { useState, useRef, useCallback, useEffect } from 'react';
import { BrowserMultiFormatReader } from '@zxing/library';
import { useAuth } from '../contexts/AuthContext';
import { useNavigate } from 'react-router-dom';
import { ref, uploadBytesResumable, getDownloadURL } from 'firebase/storage';
import { storage } from '../firebase';
import { TickSquare, CloseSquare, Image, Search } from 'react-iconly';
import { VariantEditor } from '../components/VariantEditor.tsx';
import { COLORS } from '../lib/styleGuide';
import { getTaxonomyForProductForm } from '../lib/taxonomyService';
import { addProduct } from '../utils/productApi';
import { productToasts, showError } from '../lib/utils/toast';

const ACCENT_GRAY = '#757575';
const ERROR_RED = '#F44336';





// Product information drawer
const ProductInfoDrawer = ({ isOpen, onClose, productInfo, onUpdateProductInfo, taxonomyData, variants, setVariants, videoUrl }) => {
  const [errors, setErrors] = useState({});
  const [thumbnails, setThumbnails] = useState([]);
  const [selectedThumbnail, setSelectedThumbnail] = useState(0);
  const [videoDuration, setVideoDuration] = useState(0);
  const [currentTime, setCurrentTime] = useState(0);
  const canvasRef = useRef(null);
  
  // Ingredient search state
  const [ingredientSearch, setIngredientSearch] = useState('');
  const [ingredientResults, setIngredientResults] = useState([]);
  const [isSearching, setIsSearching] = useState(false);
  const [searchError, setSearchError] = useState('');
  
  // Dietary tag search state
  const [dietaryTagSearch, setDietaryTagSearch] = useState('');
  const [dietaryTagResults, setDietaryTagResults] = useState([]);
  
  // Barcode scanning state
  const [isScanning, setIsScanning] = useState(false);
  const [scanError, setScanError] = useState('');
  const [cameraStream, setCameraStream] = useState(null);
  const [barcodeData, setBarcodeData] = useState(null);
  const [zxingReader, setZxingReader] = useState(null);
  const [detectedProduct, setDetectedProduct] = useState(null);
  const videoRef = useRef(null);
  const barcodeCanvasRef = useRef(null);

  // BARCODE RECONSTRUCTION: Try to piece together full barcode from fragments
  const [detectedFragments, setDetectedFragments] = useState([]);
  const [lastFragmentTime, setLastFragmentTime] = useState(0);
  
  // Check if fragments could form a complete EAN-13 barcode
  const checkBarcodeReconstruction = useCallback((newFragment) => {
    const now = Date.now();
    const timeWindow = 5000; // 5 seconds
    
    // Add new fragment
    setDetectedFragments(prev => {
      const updated = [...prev, { barcode: newFragment, timestamp: now }];
      
      // Keep only recent fragments
      const recent = updated.filter(f => now - f.timestamp < timeWindow);
      
      // Check if any combination could form a complete EAN-13 barcode
      const allFragments = recent.map(f => f.barcode).join('');
      
      console.log('🔍 Barcode reconstruction check:');
      console.log('  - Recent fragments:', recent.map(f => f.barcode));
      console.log('  - Combined:', allFragments);
      
      // Look for any complete EAN-13 barcode in the fragments
      const ean13Pattern = /[0-9]{13}/g;
      const matches = allFragments.match(ean13Pattern);
      
      if (matches && matches.length > 0) {
        const completeBarcode = matches[0];
        console.log('🎯 Complete EAN-13 barcode found in fragments:', completeBarcode);
        console.log('🎯 Reconstructing from:', allFragments);
        
        // Process the detected barcode
        handleBarcodeDetected(completeBarcode, 'ean_13');
        
        // Clear fragments
        return [];
      }
      
      // Check for 12-digit fragments that might be missing leading zero
      const ean12Pattern = /[0-9]{12}/g;
      const ean12Matches = allFragments.match(ean12Pattern);
      
      if (ean12Matches && ean12Matches.length > 0) {
        const ean12Barcode = ean12Matches[0];
        const restoredBarcode = '0' + ean12Barcode;
        console.log('🔧 Leading zero restoration from fragments');
        console.log('🔧 12-digit fragment:', ean12Barcode, 'Restored:', restoredBarcode);
        
        // Validate the restored barcode
        if (restoredBarcode.match(/^[0-9]{13}$/)) {
          console.log('🎯 Restored EAN-13 barcode from fragments:', restoredBarcode);
          
          // Process the restored barcode
          handleBarcodeDetected(restoredBarcode, 'ean_13');
          
          // Clear fragments
          return [];
        }
      }
      
      // Check if fragments suggest partial EAN-13
      if (allFragments.length >= 8 && allFragments.match(/^[0-9]+$/)) {
        console.log('🔍 Found partial barcode structure:', allFragments);
        console.log('🔍 This suggests a barcode is partially visible');
        console.log('🔍 Try centering the barcode better in the scan area');
      }
      
      return recent;
    });
    
    setLastFragmentTime(now);
  }, []);

  // Generate thumbnail from video at specific time
  const generateThumbnail = (time = 2) => {
    if (videoUrl && canvasRef.current) {
      const video = document.createElement('video');
      video.src = videoUrl;
      video.muted = true;
      video.crossOrigin = 'anonymous';
      
      video.onloadedmetadata = () => {
        setVideoDuration(video.duration);
        const canvas = canvasRef.current;
        const ctx = canvas.getContext('2d');
        
        // Capture frame at specified time
        video.currentTime = Math.min(time, video.duration);
        video.onseeked = () => {
          // Use 9:16 aspect ratio for thumbnail (same as video)
          const canvasWidth = 120;
          const canvasHeight = Math.round(canvasWidth * 16 / 9); // 120 * 16/9 = 213.33... ≈ 213
          
          canvas.width = canvasWidth;
          canvas.height = canvasHeight;
          
          // Calculate dimensions to maintain 9:16 aspect ratio and crop from center
          const videoAspectRatio = video.videoWidth / video.videoHeight;
          const targetAspectRatio = 9 / 16; // Target aspect ratio
          
          let sourceX, sourceY, sourceWidth, sourceHeight;
          
          if (videoAspectRatio > targetAspectRatio) {
            // Video is wider than 9:16 - crop from center
            sourceHeight = video.videoHeight;
            sourceWidth = video.videoHeight * (9 / 16); // Maintain 9:16 ratio
            sourceX = (video.videoWidth - sourceWidth) / 2; // Center horizontally
            sourceY = 0;
          } else {
            // Video is taller than 9:16 - crop from center
            sourceWidth = video.videoWidth;
            sourceHeight = video.videoWidth * (16 / 9); // Maintain 9:16 ratio
            sourceX = 0;
            sourceY = (video.videoHeight - sourceHeight) / 2; // Center vertically
          }
          
          ctx.drawImage(video, sourceX, sourceY, sourceWidth, sourceHeight, 0, 0, canvasWidth, canvasHeight);
          const thumbnailUrl = canvas.toDataURL('image/jpeg', 0.8);
          setThumbnails([thumbnailUrl]);
          // Automatically set the imageUrl when thumbnail is generated
          onUpdateProductInfo({ ...productInfo, imageUrl: thumbnailUrl });
        };
      };
    }
  };

  const validateForm = () => {
    const newErrors = {};
    if (!productInfo.name?.trim()) {
      newErrors.name = 'Name is required';
    }
    
    // Validate price
    if (!productInfo.price || isNaN(productInfo.price) || Number(productInfo.price) <= 0) {
      newErrors.price = 'Valid price is required';
    }
    
    // Validate daily inventory if days are selected
    if (productInfo.availableDays.length > 0) {
      const hasInvalidInventory = productInfo.availableDays.some(dayIndex => 
        productInfo.dailyInventory[dayIndex] === undefined || 
        productInfo.dailyInventory[dayIndex] === null || 
        isNaN(productInfo.dailyInventory[dayIndex]) || 
        Number(productInfo.dailyInventory[dayIndex]) < 0
      );
      if (hasInvalidInventory) {
        newErrors.dailyInventory = 'Please set valid inventory for all selected days.';
      }
    }
    
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSave = () => {
    if (validateForm()) {
      // Form is valid, can proceed
      onClose();
    }
  };

  const handleThumbnailSelect = () => {
    // Generate thumbnail if it doesn't exist, or regenerate with current time
    generateThumbnail(currentTime);
  };

  const handleSliderChange = (e) => {
    const newTime = parseFloat(e.target.value);
    setCurrentTime(newTime);
    generateThumbnail(newTime);
  };

  // Ingredient search functions
  const searchIngredients = async (query) => {
    if (query.length < 3) {
      setIngredientResults([]);
      return;
    }

    setIsSearching(true);
    setSearchError('');

    try {
      // Use the Open Food Facts API to search for ingredients
      let data = [];
      
      try {
        // Search for ingredients using the products API with ingredient search
        const response = await fetch(`https://world.openfoodfacts.org/cgi/search.pl?search_terms=${encodeURIComponent(query)}&search_simple=1&action=process&json=1&page_size=50&fields=ingredients_text,product_name,brands,nutriments,image_url`);
        
        if (!response.ok) {
          throw new Error('Failed to fetch ingredients');
        }
        
        const apiData = await response.json();
        console.log('API response data:', apiData);
        
        if (apiData && apiData.products && apiData.products.length > 0) {
          // Extract and process ingredients from products
          const ingredientsMap = new Map();
          
          apiData.products.forEach(product => {
            if (product.ingredients_text) {
              // Split ingredients by common separators
              const ingredients = product.ingredients_text
                .split(/[,;()]/)
                .map(ing => ing.trim())
                .filter(ing => ing.length > 0 && ing.toLowerCase().includes(query.toLowerCase()));
              
              ingredients.forEach(ingredient => {
                const key = ingredient.toLowerCase();
                if (!ingredientsMap.has(key)) {
                  ingredientsMap.set(key, {
                    id: `en:${key.replace(/\s+/g, '-').replace(/[^a-z0-9-]/g, '')}`,
                    text: ingredient,
                    products: 1,
                    vegetarian: 'yes', // Default assumption
                    vegan: 'yes', // Default assumption
                    brand: product.brands || 'Unknown',
                    calories: product.nutriments?.energy_100g || 0,
                    imageUrl: product.image_url
                  });
                } else {
                  // Increment product count for existing ingredient
                  const existing = ingredientsMap.get(key);
                  existing.products += 1;
                  // Update brand if this product has a better one
                  if (product.brands && !existing.brand.includes(product.brands)) {
                    existing.brand = existing.brand + ', ' + product.brands;
                  }
                }
              });
            }
          });
          
          // Convert map to array and sort by popularity
          data = Array.from(ingredientsMap.values())
            .sort((a, b) => b.products - a.products)
            .slice(0, 20); // Top 20 results
          
          console.log('Processed ingredients from API:', data);
        }
        
        // If no results from API, fall back to mock data
        if (data.length === 0) {
          console.log('No API results, using mock data fallback');
          data = getMockIngredientsData();
        }
        
      } catch (apiError) {
        console.warn('API fetch failed, using mock data:', apiError);
        // Fallback to mock data for testing
        data = getMockIngredientsData();
        console.log('Using mock data fallback:', data);
      }

      if (data && data.length > 0) {
        console.log('Filtering ingredients for query:', query);
        console.log('Available ingredients:', data.slice(0, 3)); // Log first 3 for debugging
        
        // Filter ingredients that match the query
        const matchingIngredients = data.filter(ingredient => {
          const searchText = query.toLowerCase();
          const ingredientText = (ingredient.text || ingredient.name || '').toLowerCase();
          const ingredientId = (ingredient.id || '').toLowerCase();
          
          console.log('Checking ingredient:', ingredientText, 'against query:', searchText);
          
          // Check if query matches ingredient text or ID
          const matches = ingredientText.includes(searchText) || 
                         ingredientId.includes(searchText) ||
                         // Handle common synonyms and abbreviations
                         (searchText === 'flour' && ingredientText.includes('flour')) ||
                         (searchText === 'sugar' && ingredientText.includes('sugar')) ||
                         (searchText === 'salt' && ingredientText.includes('salt')) ||
                         (searchText === 'oil' && ingredientText.includes('oil')) ||
                         (searchText === 'milk' && ingredientText.includes('milk')) ||
                         (searchText === 'egg' && ingredientText.includes('egg'));
          
          if (matches) {
            console.log('Match found:', ingredientText);
          }
          
          return matches;
        });
        
        console.log('Matching ingredients found:', matchingIngredients.length);

        if (matchingIngredients.length > 0) {
          // Sort by popularity (products count) and relevance
          const rankedIngredients = matchingIngredients
            .map(ingredient => ({
              ...ingredient,
              // Calculate popularity score based on products count and relevance
              popularityScore: calculatePopularityScore(ingredient, query)
            }))
            .sort((a, b) => b.popularityScore - a.popularityScore)
            .slice(0, 10); // Return top 10 results

          const formattedResults = rankedIngredients.map(ingredient => ({
            id: ingredient.id || `en:${ingredient.text?.toLowerCase().replace(/\s+/g, '-').replace(/[^a-z0-9-]/g, '')}`,
            name: ingredient.text || ingredient.name || 'Unknown Ingredient',
            vegetarian: ingredient.vegetarian === 'yes',
            vegan: ingredient.vegan === 'yes',
            productsCount: ingredient.products || 0
          }));
          
          setIngredientResults(formattedResults);
        } else {
          // No matching ingredients found
          setIngredientResults([]);
        }
      } else {
        setIngredientResults([]);
      }
    } catch (error) {
      console.error('Error searching ingredients:', error);
      // Fallback: create mock results based on the search query
      const mockResults = [
        {
          id: `en:${query.toLowerCase().replace(/\s+/g, '-')}`,
          name: query.charAt(0).toUpperCase() + query.slice(1),
          vegetarian: true,
          vegan: true,
          productsCount: 1
        }
      ];
      setIngredientResults(mockResults);
    } finally {
      setIsSearching(false);
    }
  };

  // Calculate popularity score for ingredient ranking
  const calculatePopularityScore = (ingredient, query) => {
    let score = 0;
    
    // Base score from products count (primary factor)
    const productsCount = ingredient.products || 0;
    score += productsCount * 10;
    
    // Relevance bonus for exact matches
    const searchText = query.toLowerCase();
    const ingredientText = (ingredient.text || ingredient.name || '').toLowerCase();
    const ingredientId = (ingredient.id || '').toLowerCase();
    
    // Exact text match gets highest bonus
    if (ingredientText === searchText) {
      score += 1000;
    }
    // Starts with query gets high bonus
    else if (ingredientText.startsWith(searchText)) {
      score += 500;
    }
    // Contains query gets medium bonus
    else if (ingredientText.includes(searchText)) {
      score += 200;
    }
    
    // ID match bonus
    if (ingredientId.includes(searchText)) {
      score += 100;
    }
    
    // Common ingredient bonus (recognize frequently used ingredients)
    const commonIngredients = [
      'flour', 'sugar', 'salt', 'oil', 'milk', 'egg', 'butter', 'cheese',
      'tomato', 'onion', 'garlic', 'lemon', 'vanilla', 'chocolate', 'bread'
    ];
    
    if (commonIngredients.some(common => ingredientText.includes(common))) {
      score += 50;
    }
    
    return score;
  };

  // Log ingredient usage for popularity tracking
  const logIngredientUsage = async (ingredient) => {
    try {
      // In a real implementation, this would save to Firebase
      // For now, we'll log to console and could implement Firebase logging later
      console.log('Ingredient selected:', {
        id: ingredient.id,
        name: ingredient.name,
        timestamp: new Date().toISOString(),
        productsCount: ingredient.productsCount || 0,
        barcode: barcodeData?.barcode || null
      });
      
      // TODO: Implement Firebase logging for ingredient usage tracking
      // This would allow us to weight local platform usage in popularity scoring
      // await addDoc(collection(db, 'ingredient_usage'), {
      //   ingredientId: ingredient.id,
      //   ingredientName: ingredient.name,
      //   selectedAt: new Date().toISOString(),
      //   barcode: barcodeData?.barcode || null,
      //   sellerId: currentUser?.uid || 'anonymous'
      // });
    } catch (error) {
      console.error('Error logging ingredient usage:', error);
      // Don't fail the ingredient addition if logging fails
    }
  };

  // Monitor canvas state for debugging
  useEffect(() => {
    if (barcodeCanvasRef.current) {
      console.log('Canvas ref updated, dimensions:', barcodeCanvasRef.current.width, 'x', barcodeCanvasRef.current.height);
    }
  }, [barcodeCanvasRef.current]);

  // Monitor scanning state for debugging
  useEffect(() => {
    console.log('🔄 isScanning state changed to:', isScanning);
    // Add stack trace to see what's calling the state change
    console.trace('isScanning state change stack trace:');
  }, [isScanning]);

  // Cleanup ZXing reader on component unmount
  useEffect(() => {
    return () => {
      if (zxingReader) {
        try {
          zxingReader.reset();
          console.log('🧹 ZXing reader cleaned up on unmount');
        } catch (error) {
          console.log('⚠️ Error cleaning up ZXing reader:', error.message);
        }
      }
    };
  }, [zxingReader]);

  // Manual barcode entry handler
  const handleManualBarcodeSubmit = async (barcode) => {
    if (barcode.length >= 8 && barcode.match(/^[0-9]+$/)) {
      console.log('📝 Manual barcode entry submitted:', barcode);
      handleBarcodeDetected(barcode, 'manual_entry');
      setScanError('Manual barcode entered. Looking up product...');
    } else {
      setScanError('Please enter a valid numeric barcode (at least 8 digits).');
    }
  };

  // Barcode scanning functions
  const startBarcodeScan = async () => {
    try {
      setScanError('');
      setIsScanning(true);
      setDetectedProduct(null);
      setBarcodeData(null);
      
      console.log('🔍 Requesting camera access...');
      
      // Request camera access with more flexible constraints
      const stream = await navigator.mediaDevices.getUserMedia({ 
        video: { 
          facingMode: 'environment', // Use back camera on mobile
          width: { min: 640, ideal: 1280, max: 1920 },
          height: { min: 480, ideal: 720, max: 1080 }
        } 
      });
      
      console.log('✅ Camera access granted, stream:', stream);
      
      // Set camera stream state immediately
      setCameraStream(stream);
      
      // Start barcode detection
      if (videoRef.current) {
        console.log('🎥 Setting video source and playing...');
        videoRef.current.srcObject = stream;
        
        // Wait for video to be ready before starting detection
        videoRef.current.onloadedmetadata = () => {
          console.log('🎥 Video metadata loaded, starting barcode detection...');
          startBarcodeDetection(stream);
        };
        
        videoRef.current.onerror = (error) => {
          console.error('❌ Video error:', error);
          setScanError('Video playback error. Please try again.');
        };
        
        // Start playing the video
        try {
          await videoRef.current.play();
          console.log('✅ Video started playing successfully');
        } catch (playError) {
          console.error('❌ Video play error:', playError);
          setScanError('Failed to start video. Please try again.');
          setIsScanning(false);
          return;
        }
        
        // Start health monitoring for the camera stream
        startCameraHealthCheck(stream);
        
      } else {
        console.error('❌ Video ref not available');
        setScanError('Video element not available. Please restart the scanner.');
        setIsScanning(false);
      }
      
    } catch (error) {
      console.error('❌ Camera access error:', error);
      setIsScanning(false);
      
      if (error.name === 'NotAllowedError') {
        setScanError('Camera access denied. Please enable camera permissions and try again.');
      } else if (error.name === 'NotFoundError') {
        setScanError('No camera found. Please check your device has a camera.');
      } else if (error.name === 'NotReadableError') {
        setScanError('Camera is in use by another application. Please close other camera apps and try again.');
      } else if (error.name === 'OverconstrainedError') {
        setScanError('Camera constraints not supported. Trying with basic settings...');
        // Try again with basic constraints
        try {
          const basicStream = await navigator.mediaDevices.getUserMedia({ 
            video: true 
          });
          setCameraStream(basicStream);
          if (videoRef.current) {
            videoRef.current.srcObject = basicStream;
            await videoRef.current.play();
            startBarcodeDetection(basicStream);
            startCameraHealthCheck(basicStream);
          }
        } catch (basicError) {
          console.error('❌ Basic camera access also failed:', basicError);
          setScanError('Camera access failed. Please check permissions and try again.');
        }
      } else {
        setScanError(`Camera error: ${error.message}. Please try again.`);
      }
    }
  };

  const startCameraHealthCheck = (stream) => {
    // Monitor camera stream health but don't auto-restart
    // This prevents the problematic restart logic that causes errors
    const healthCheckInterval = setInterval(() => {
      if (!isScanning || !stream) {
        clearInterval(healthCheckInterval);
        return;
      }
      
      // Check if stream tracks are still active
      const activeTracks = stream.getTracks().filter(track => track.readyState === 'live');
      if (activeTracks.length === 0) {
        console.warn('⚠️ Camera stream tracks are not live, but not auto-restarting');
        // Don't auto-restart - let user use manual restart button if needed
        clearInterval(healthCheckInterval);
      }
    }, 3000); // Check every 3 seconds
  };





  const isBarcodeInFocusArea = (barcode) => {
    // Check if barcode bounding box is within the focus area (green box)
    // The focus area is roughly the center 60% of the video frame
    if (!barcode.boundingBox) {
      console.log('No bounding box data for barcode - assuming valid');
      return true; // Assume valid if no bounding box data
    }
    
    const video = videoRef.current;
    if (!video) {
      console.log('No video ref - assuming valid');
      return true;
    }
    
    const videoWidth = video.videoWidth;
    const videoHeight = video.videoHeight;
    
    if (!videoWidth || !videoHeight) {
      console.log('Video dimensions not available - assuming valid');
      return true;
    }
    
    // Define focus area (center 60% of frame)
    const focusAreaWidth = videoWidth * 0.6;
    const focusAreaHeight = videoHeight * 0.6;
    const focusAreaX = (videoWidth - focusAreaWidth) / 2;
    const focusAreaY = (videoHeight - focusAreaHeight) / 2;
    
    // Check if barcode is within focus area
    const barcodeBox = barcode.boundingBox;
    const barcodeCenterX = (barcodeBox.x + barcodeBox.x + barcodeBox.width) / 2;
    const barcodeCenterY = (barcodeBox.y + barcodeBox.y + barcodeBox.height) / 2;
    
    const inFocusArea = (
      barcodeCenterX >= focusAreaX &&
      barcodeCenterX <= focusAreaX + focusAreaWidth &&
      barcodeCenterY >= focusAreaY &&
      barcodeCenterY <= focusAreaY + focusAreaHeight
    );
    
    console.log('Focus area validation:', {
      videoDimensions: `${videoWidth}x${videoHeight}`,
      focusArea: `${focusAreaX},${focusAreaY} ${focusAreaWidth}x${focusAreaHeight}`,
      barcodeCenter: `${barcodeCenterX},${barcodeCenterY}`,
      inFocusArea
    });
    
    // For now, be more lenient and accept barcodes even if not perfectly in focus area
    // This helps with testing and real-world usage where positioning might not be perfect
    if (!inFocusArea) {
      console.log('⚠️ Barcode not perfectly in focus area, but accepting anyway for testing');
      return true;
    }
    
    return inFocusArea;
  };

  const stopBarcodeDetection = () => {
    console.log('🛑 stopBarcodeDetection called - stopping ZXing detection');
    
    // Stop ZXing detection
    if (zxingReader) {
      try {
        zxingReader.reset();
        console.log('✅ ZXing detection stopped');
      } catch (error) {
        console.log('ZXing already stopped or not running');
      }
    }
    
    // Stop camera stream
    if (cameraStream) {
      cameraStream.getTracks().forEach(track => {
        track.stop();
        console.log('Camera track stopped:', track.kind);
      });
      setCameraStream(null);
    }
    
    console.log('Barcode detection stopped and camera stream terminated');
  };



  const stopBarcodeScan = () => {
    console.log('🛑 stopBarcodeScan called - user cancelled scanning');
    console.log('🔍 Stack trace for stopBarcodeScan call:');
    console.trace();
    
    // Stop ZXing detection
    if (zxingReader) {
      try {
        zxingReader.reset();
        console.log('✅ ZXing detection stopped');
      } catch (error) {
        console.log('ZXing already stopped or not running');
      }
    }
    
    if (cameraStream) {
      cameraStream.getTracks().forEach(track => track.stop());
      setCameraStream(null);
    }
    setIsScanning(false);
    setScanError('');
    setBarcodeData(null);
  };

  const startBarcodeDetection = async (stream) => {
    console.log('🚀 Starting ZXing barcode detection...');
    console.log('🔍 Stream parameter:', stream);
    
    // Check if video ref and stream are available
    if (!videoRef.current) {
      console.error('❌ Video ref not available for ZXing initialization');
      setScanError('Camera not available. Please restart the scanner.');
      return;
    }
    
    if (!stream) {
      console.error('❌ Stream parameter not available for ZXing initialization');
      setScanError('Camera stream not available. Please restart the scanner.');
      return;
    }
    
    // Check if stream is still active
    const activeTracks = stream.getTracks().filter(track => track.readyState === 'live');
    if (activeTracks.length === 0) {
      console.error('❌ Stream tracks are not live');
      setScanError('Camera stream inactive. Please restart the scanner.');
      return;
    }
    
    console.log('✅ Camera checks passed, initializing ZXing...');
    
    try {
      // Check if ZXing is available
      if (typeof BrowserMultiFormatReader === 'undefined') {
        throw new Error('ZXing library not loaded');
      }
      
      // Create new ZXing reader
      const reader = new BrowserMultiFormatReader();
      setZxingReader(reader);
      
      console.log('✅ ZXing reader created successfully');
      
      // Make sure video element is properly configured
      if (!videoRef.current.srcObject) {
        videoRef.current.srcObject = stream;
      }
      
      // Wait for video to be ready
      if (videoRef.current.readyState < 2) { // HAVE_CURRENT_DATA
        console.log('⏳ Waiting for video to be ready...');
        await new Promise((resolve) => {
          videoRef.current.oncanplay = resolve;
          videoRef.current.onloadeddata = resolve;
        });
      }
      
      console.log('✅ Video ready, starting ZXing scanning...');
      
      // Start continuous scanning
      const scanBarcode = async () => {
        if (!isScanning || !reader) return;
        
        try {
          // Try to decode barcode from current video frame
          const result = await reader.decodeFromVideoElement(videoRef.current);
          
          if (result) {
            console.log('🎯 ZXing: Barcode detected!');
            console.log('🔍 Raw detection result:', result);
            console.log('🔍 Raw value:', result.text);
            console.log('🔍 Format:', result.format);
            console.log('🔍 Barcode detection event triggered');
            
            // Log the exact barcode data received
            const rawBarcode = result.text;
            console.log('🔍 Raw barcode received:', rawBarcode);
            console.log('🔍 Barcode length:', rawBarcode.length);
            console.log('🔍 Barcode type:', typeof rawBarcode);
            console.log('🔍 Barcode characters:', rawBarcode.split('').map((char, i) => `${i}:${char}`).join(' '));
            
            // Process the detected barcode
            const barcode = result.text;
            const format = result.format;
            
            // Handle EAN-13, UPC, and other formats
            if (format === 'EAN_13' || format === 'UPC_A' || format === 'CODE_128') {
              console.log('🎯 Valid barcode detected! Pausing camera and looking up product...');
              console.log('🎯 Barcode:', barcode, 'Format:', format);
              
              // Stop scanning
              setIsScanning(false);
              
              // Process the detected barcode
              handleBarcodeDetected(barcode, format.toLowerCase());
              
              // Show success message
              setScanError('🎯 Barcode detected! Looking up product...');
              
              return; // Stop scanning
            } else {
              console.log('⚠️ Unsupported format detected:', format, 'Barcode:', barcode);
              // Continue scanning for supported formats
            }
          }
        } catch (error) {
          // No barcode detected in this frame - continue scanning
          if (error.name === 'NotFoundException') {
            // This is normal - no barcode in frame
          } else {
            console.log('⚠️ ZXing scan error:', error.message);
          }
        }
        
        // Continue scanning if still active
        if (isScanning) {
          setTimeout(scanBarcode, 100); // Scan every 100ms
        }
      };
      
      // Start scanning
      scanBarcode();
      
    } catch (error) {
      console.error('❌ ZXing initialization error:', error);
      setScanError('Failed to initialize camera for barcode scanning');
    }
      

  };






  const handleBarcodeDetected = async (barcode, format) => {
    try {
      // Prevent duplicate detections
      if (barcodeData) {
        console.log('Barcode already processed, ignoring duplicate detection');
        return;
      }
      
      console.log('🎯 Processing barcode detection:', barcode, format);
      console.log('🔍 Barcode type:', typeof barcode);
      console.log('🔍 Barcode length:', barcode?.length);
      console.log('🔍 Barcode format:', format);
      console.log('🔍 Raw barcode data:', barcode);
      
      // Show what barcode was detected
      setScanError(`🔍 Detected barcode: ${barcode} (${format})`);
      
      setBarcodeData({ barcode, format });
      
      // Show lookup in progress message
      setTimeout(() => {
        setScanError('🔍 Looking up product information...');
      }, 1000);
      
      // Fetch product data from Open Food Facts API
      const productData = await fetchProductByBarcode(barcode);
      
      if (productData) {
        console.log('✅ Product found for barcode:', productData.product_name);
        
        // Store the detected product data
        setDetectedProduct(productData);
        
        // Show success message with product found
        setScanError(`✅ Product found: ${productData.product_name}`);
        
        // Keep modal open for user to decide whether to add
        console.log('🎯 Product found successfully, waiting for user to add');
      } else {
        console.log('❌ No product found for barcode:', barcode);
        setScanError('No product found for this barcode. Try scanning a different barcode or search by name instead.');
        
                                  // Clear barcode data and let ZXing continue scanning
                          // No need to restart - ZXing is already running
        setTimeout(() => {
          console.log('🔄 Clearing barcode data for next scan attempt');
          setBarcodeData(null);
          setScanError('Ready for next scan. Try a different barcode.');
        }, 1000); // Wait 1 second before clearing
      }
      
    } catch (error) {
      console.error('Error handling barcode:', error);
      setScanError('Failed to fetch product data. Please try again.');
    }
  };

  const addDetectedProductToIngredients = () => {
    if (!detectedProduct) return;
    
    try {
      // Convert product data to ingredient format
      const ingredient = {
        id: `en:${detectedProduct.product_name?.toLowerCase().replace(/\s+/g, '-').replace(/[^a-z0-9-]/g, '')}`,
        name: detectedProduct.product_name || 'Unknown Product',
        vegetarian: detectedProduct.ingredients_analysis_tags?.includes('en:vegetarian') ? 'yes' : 'yes',
        vegan: detectedProduct.ingredients_analysis_tags?.includes('en:vegan') ? 'yes' : 'yes',
        productsCount: 1,
        brand: detectedProduct.brands || 'Unknown',
        calories: detectedProduct.nutriments?.energy_100g || 0,
        imageUrl: detectedProduct.image_url,
        barcode: detectedProduct.code || barcodeData?.barcode
      };
      
      // Add ingredient to the list
      addIngredient(ingredient);
      
      // Show success message
      setScanError('✅ Product added to ingredients!');
      
      // Clear the detected product and barcode data
      setTimeout(() => {
        setDetectedProduct(null);
        setBarcodeData(null);
        setScanError('Ready for next scan. Try a different barcode.');
      }, 2000);
      
      console.log('🎯 Product added to ingredients:', ingredient);
      
    } catch (error) {
      console.error('Error adding product to ingredients:', error);
      setScanError('Failed to add product to ingredients. Please try again.');
    }
  };

  const fetchProductByBarcode = async (barcode) => {
    try {
      console.log('🔍 Starting product lookup for barcode:', barcode);
      
      // Primary API: Open Food Facts
      console.log('📡 Fetching from Open Food Facts API...');
      const response = await fetch(`https://world.openfoodfacts.org/api/v0/product/${barcode}.json`);
      
      if (!response.ok) {
        console.log('❌ Open Food Facts API response not OK:', response.status, response.statusText);
        throw new Error('Failed to fetch product data');
      }
      
      const data = await response.json();
      console.log('📊 Open Food Facts API response:', data);
      console.log('🔍 Response status:', data.status);
      console.log('🔍 Product data:', data.product);
      console.log('🔍 Full response structure:', Object.keys(data));
      
      if (data.status === 1 && data.product) {
        console.log('✅ Product found in Open Food Facts:', data.product.product_name);
        console.log('🔍 Product details:', {
          name: data.product.product_name,
          brand: data.product.brands,
          barcode: data.product.code,
          image: data.product.image_url ? 'Available' : 'Not available'
        });
        return data.product;
      } else {
        console.log('⚠️ No product found in Open Food Facts, status:', data.status);
        if (data.status === 0) {
          console.log('🔍 Status 0 means: Product not found or barcode not in database');
          console.log('🔍 This could mean:');
          console.log('   - Barcode format is different than expected');
          console.log('   - Product is not in Open Food Facts database');
          console.log('   - API endpoint format has changed');
          console.log('🔍 Barcode searched:', barcode);
          console.log('🔍 API response structure:', Object.keys(data));
        }
      }
      
      // Note: UPCitemDB API removed due to CORS issues
      // Open Food Facts is the primary and most reliable source
      console.log('ℹ️ Skipping UPCitemDB API due to CORS restrictions');
      
      console.log('❌ No product found in any API for barcode:', barcode);
      return null;
      
    } catch (error) {
      console.error('❌ Error fetching product by barcode:', error);
      return null;
    }
  };

  // Mock ingredients data for testing when API is unavailable
  const getMockIngredientsData = () => {
    return [
      // Flour varieties
      {
        id: 'en:all-purpose-flour',
        text: 'All Purpose Flour',
        products: 1500,
        vegetarian: 'yes',
        vegan: 'yes'
      },
      {
        id: 'en:whole-wheat-flour',
        text: 'Whole Wheat Flour',
        products: 800,
        vegetarian: 'yes',
        vegan: 'yes'
      },
      {
        id: 'en:bread-flour',
        text: 'Bread Flour',
        products: 600,
        vegetarian: 'yes',
        vegan: 'yes'
      },
      {
        id: 'en:cake-flour',
        text: 'Cake Flour',
        products: 400,
        vegetarian: 'yes',
        vegan: 'yes'
      },
      {
        id: 'en:almond-flour',
        text: 'Almond Flour',
        products: 300,
        vegetarian: 'yes',
        vegan: 'yes'
      },
      {
        id: 'en:coconut-flour',
        text: 'Coconut Flour',
        products: 200,
        vegetarian: 'yes',
        vegan: 'yes'
      },
      {
        id: 'en:rye-flour',
        text: 'Rye Flour',
        products: 150,
        vegetarian: 'yes',
        vegan: 'yes'
      },
      {
        id: 'en:spelt-flour',
        text: 'Spelt Flour',
        products: 100,
        vegetarian: 'yes',
        vegan: 'yes'
      },
      {
        id: 'en:buckwheat-flour',
        text: 'Buckwheat Flour',
        products: 80,
        vegetarian: 'yes',
        vegan: 'yes'
      },
      {
        id: 'en:quinoa-flour',
        text: 'Quinoa Flour',
        products: 60,
        vegetarian: 'yes',
        vegan: 'yes'
      },
      // Eggs
      {
        id: 'en:egg',
        text: 'Egg',
        products: 2000,
        vegetarian: 'yes',
        vegan: 'no'
      },
      {
        id: 'en:egg-white',
        text: 'Egg White',
        products: 800,
        vegetarian: 'yes',
        vegan: 'no'
      },
      {
        id: 'en:egg-yolk',
        text: 'Egg Yolk',
        products: 600,
        vegetarian: 'yes',
        vegan: 'no'
      },
      {
        id: 'en:large-egg',
        text: 'Large Egg',
        products: 1200,
        vegetarian: 'yes',
        vegan: 'no'
      },
      {
        id: 'en:free-range-egg',
        text: 'Free Range Egg',
        products: 400,
        vegetarian: 'yes',
        vegan: 'no'
      },
      // Sugar varieties
      {
        id: 'en:granulated-sugar',
        text: 'Granulated Sugar',
        products: 1800,
        vegetarian: 'yes',
        vegan: 'yes'
      },
      {
        id: 'en:brown-sugar',
        text: 'Brown Sugar',
        products: 1200,
        vegetarian: 'yes',
        vegan: 'yes'
      },
      {
        id: 'en:powdered-sugar',
        text: 'Powdered Sugar',
        products: 900,
        vegetarian: 'yes',
        vegan: 'yes'
      },
      {
        id: 'en:coconut-sugar',
        text: 'Coconut Sugar',
        products: 300,
        vegetarian: 'yes',
        vegan: 'yes'
      },
      {
        id: 'en:maple-syrup',
        text: 'Maple Syrup',
        products: 500,
        vegetarian: 'yes',
        vegan: 'yes'
      },
      // Salt and seasonings
      {
        id: 'en:table-salt',
        text: 'Table Salt',
        products: 1600,
        vegetarian: 'yes',
        vegan: 'yes'
      },
      {
        id: 'en:sea-salt',
        text: 'Sea Salt',
        products: 800,
        vegetarian: 'yes',
        vegan: 'yes'
      },
      {
        id: 'en:kosher-salt',
        text: 'Kosher Salt',
        products: 600,
        vegetarian: 'yes',
        vegan: 'yes'
      },
      {
        id: 'en:black-pepper',
        text: 'Black Pepper',
        products: 1000,
        vegetarian: 'yes',
        vegan: 'yes'
      },
      {
        id: 'en:garlic-powder',
        text: 'Garlic Powder',
        products: 700,
        vegetarian: 'yes',
        vegan: 'yes'
      },
      // Oils and fats
      {
        id: 'en:olive-oil',
        text: 'Olive Oil',
        products: 1400,
        vegetarian: 'yes',
        vegan: 'yes'
      },
      {
        id: 'en:vegetable-oil',
        text: 'Vegetable Oil',
        products: 1100,
        vegetarian: 'yes',
        vegan: 'yes'
      },
      {
        id: 'en:coconut-oil',
        text: 'Coconut Oil',
        products: 600,
        vegetarian: 'yes',
        vegan: 'yes'
      },
      {
        id: 'en:butter',
        text: 'Butter',
        products: 1300,
        vegetarian: 'yes',
        vegan: 'no'
      },
      {
        id: 'en:unsalted-butter',
        text: 'Unsalted Butter',
        products: 900,
        vegetarian: 'yes',
        vegan: 'no'
      },
      // Dairy
      {
        id: 'en:milk',
        text: 'Milk',
        products: 1500,
        vegetarian: 'yes',
        vegan: 'no'
      },
      {
        id: 'en:whole-milk',
        text: 'Whole Milk',
        products: 800,
        vegetarian: 'yes',
        vegan: 'no'
      },
      {
        id: 'en:skim-milk',
        text: 'Skim Milk',
        products: 600,
        vegetarian: 'yes',
        vegan: 'no'
      },
      {
        id: 'en:cheese',
        text: 'Cheese',
        products: 1200,
        vegetarian: 'yes',
        vegan: 'no'
      },
      {
        id: 'en:cheddar-cheese',
        text: 'Cheddar Cheese',
        products: 800,
        vegetarian: 'yes',
        vegan: 'no'
      },
      {
        id: 'en:parmesan-cheese',
        text: 'Parmesan Cheese',
        products: 500,
        vegetarian: 'yes',
        vegan: 'no'
      }
    ];
  };

  const addIngredient = (ingredient) => {
    // Check if ingredient already exists
    if (productInfo.ingredients.some(ing => ing.id === ingredient.id)) {
      return;
    }
    
    // Log ingredient usage for popularity tracking
    logIngredientUsage(ingredient);
    
    const newIngredients = [...productInfo.ingredients, ingredient];
    onUpdateProductInfo({ ...productInfo, ingredients: newIngredients });
    setIngredientSearch('');
    setIngredientResults([]);
  };

  const removeIngredient = (ingredientId) => {
    const newIngredients = productInfo.ingredients.filter(ing => ing.id !== ingredientId);
    onUpdateProductInfo({ ...productInfo, ingredients: newIngredients });
  };

  // Dietary tag functions
  const addDietaryTag = (tag) => {
    const currentTags = productInfo.dietaryTags || [];
    if (!currentTags.includes(tag.value)) {
      const newTags = [...currentTags, tag.value];
      onUpdateProductInfo({ ...productInfo, dietaryTags: newTags });
    }
    setDietaryTagSearch('');
    setDietaryTagResults([]);
  };

  const removeDietaryTag = (tagValue) => {
    const currentTags = productInfo.dietaryTags || [];
    const updatedTags = currentTags.filter(t => t !== tagValue);
    onUpdateProductInfo({ ...productInfo, dietaryTags: updatedTags });
  };

  // Debounced search effect
  useEffect(() => {
    const timeoutId = setTimeout(() => {
      if (ingredientSearch.trim()) {
        searchIngredients(ingredientSearch.trim());
      } else {
        setIngredientResults([]);
      }
    }, 300);

    return () => clearTimeout(timeoutId);
  }, [ingredientSearch]);

  // Dietary tag search effect
  useEffect(() => {
    if (dietaryTagSearch.trim() && taxonomyData?.dietaryTags?.values) {
      const searchTerm = dietaryTagSearch.toLowerCase();
      const filteredTags = taxonomyData.dietaryTags.values.filter(tag => 
        tag.display?.toLowerCase().includes(searchTerm) || 
        tag.value.toLowerCase().includes(searchTerm)
      );
      setDietaryTagResults(filteredTags);
    } else {
      setDietaryTagResults([]);
    }
  }, [dietaryTagSearch, taxonomyData?.dietaryTags?.values]);

  // Note: Removed automatic timeout - scanner stays open until user closes it
  // This prevents the scanner from stopping unexpectedly after 10 seconds

  // HYBRID APPROACH: Manual barcode detection as backup
  const tryManualBarcodeDetection = useCallback(() => {
    if (!videoRef.current || !isScanning) return;
    
    try {
      // Create a canvas to analyze the current video frame
      const canvas = document.createElement('canvas');
      const ctx = canvas.getContext('2d');
      
      // Set canvas size to video size
      canvas.width = videoRef.current.videoWidth;
      canvas.height = videoRef.current.videoHeight;
      
      // Draw current video frame to canvas
      ctx.drawImage(videoRef.current, 0, 0, canvas.width, canvas.height);
      
      // Get image data for analysis
      const imageData = ctx.getImageData(0, 0, canvas.width, canvas.height);
      
      // Enhanced barcode detection: look for high contrast lines with lower threshold
      const data = imageData.data;
      let barcodeLines = 0;
      let totalPixels = data.length / 4;
      
      // Count potential barcode lines (high contrast transitions) - LOWER THRESHOLD
      for (let i = 0; i < data.length; i += 4) {
        const r = data[i];
        const g = data[i + 1];
        const b = data[i + 2];
        
        // Calculate brightness
        const brightness = (r + g + b) / 3;
        
        // Look for black/white transitions (potential barcode) - MORE SENSITIVE
        if (brightness < 80 || brightness > 180) { // Lowered threshold from 50/200
          barcodeLines++;
        }
      }
      
      // If we detect enough contrast, try to extract barcode
      const contrastRatio = barcodeLines / totalPixels;
      const lowerThreshold = 0.15; // Lowered from 0.3
      
      if (contrastRatio > lowerThreshold) {
        console.log('🔍 Manual detection: Sufficient contrast detected, attempting barcode extraction...');
        console.log('🔍 Contrast ratio:', contrastRatio.toFixed(3), '(need >', lowerThreshold, ')');
        
        // Try to find the actual barcode in the image
        // For now, we'll use a simple approach: look for the center area
        const centerX = Math.floor(canvas.width / 2);
        const centerY = Math.floor(canvas.height / 2);
        const scanSize = Math.min(canvas.width, canvas.height) * 0.5; // 50% of smaller dimension (increased from 40%)
        
        // Extract center region
        const centerData = ctx.getImageData(
          centerX - scanSize/2, 
          centerY - scanSize/2, 
          scanSize, 
          scanSize
        );
        
        // Analyze center region for barcode patterns
        analyzeCenterRegion(centerData, scanSize);
      } else {
        console.log('🔍 Manual detection: Low contrast - barcode may not be clearly visible');
        console.log('🔍 Contrast ratio:', contrastRatio.toFixed(3), '(need >', lowerThreshold, ')');
        console.log('🔍 Try improving lighting or getting closer to the barcode');
        console.log('🔍 Or use the manual entry field below');
        
        // Set error message to guide user
        setScanError(`Low contrast detected (${contrastRatio.toFixed(3)}). Try better lighting or use manual entry.`);
      }
      
    } catch (error) {
      console.log('⚠️ Manual barcode detection error:', error.message);
    }
  }, [isScanning]);
  
  // Analyze center region for barcode patterns
  const analyzeCenterRegion = useCallback((imageData, size) => {
    try {
      const data = imageData.data;
      const width = size;
      const height = size;
      
      // Look for horizontal lines (typical barcode structure)
      let horizontalLines = 0;
      let verticalLines = 0;
      
      // Count horizontal transitions with LOWER THRESHOLD
      for (let y = 0; y < height; y++) {
        let transitions = 0;
        for (let x = 1; x < width; x++) {
          const idx = (y * width + x) * 4;
          const prevIdx = (y * width + (x - 1)) * 4;
          
          const currBrightness = (data[idx] + data[idx + 1] + data[idx + 2]) / 3;
          const prevBrightness = (data[prevIdx] + data[prevIdx + 1] + data[prevIdx + 2]) / 3;
          
          // Count significant brightness changes - MORE SENSITIVE
          if (Math.abs(currBrightness - prevBrightness) > 50) { // Lowered from 100
            transitions++;
          }
        }
        
        if (transitions > width * 0.05) { // Lowered from 0.1 (5% instead of 10%)
          horizontalLines++;
        }
      }
      
      // If we detect enough horizontal structure, it might be a barcode
      const structureThreshold = 0.2; // Lowered from 0.3
      if (horizontalLines > height * structureThreshold) {
        console.log('🔍 Manual detection: Barcode-like structure detected in center region');
        console.log('🔍 Horizontal lines detected:', horizontalLines, 'out of', height);
        console.log('🔍 Structure ratio:', (horizontalLines / height).toFixed(3), '(need >', structureThreshold, ')');
        
        // Since we can't decode the actual barcode from image data,
        // we'll prompt the user to try manual entry
        setScanError('🔍 Barcode structure detected! Keep barcode steady in center area for auto-detection, or use manual entry.');
      } else {
        console.log('🔍 Manual detection: Insufficient barcode structure in center region');
        console.log('🔍 Horizontal lines detected:', horizontalLines, 'out of', height);
        console.log('🔍 Structure ratio:', (horizontalLines / height).toFixed(3), '(need >', structureThreshold, ')');
        console.log('🔍 Try centering the barcode better, improving lighting, or use manual entry');
        
        setScanError(`Insufficient barcode structure (${(horizontalLines / height).toFixed(3)}). Try better positioning or use manual entry.`);
      }
      
    } catch (error) {
      console.log('⚠️ Center region analysis error:', error.message);
    }
  }, []);
  
  // CONTINUOUS MANUAL DETECTION: Run every 3 seconds as backup
  useEffect(() => {
    if (!isScanning) return;
    
    const manualDetectionInterval = setInterval(() => {
      if (isScanning && videoRef.current) {
        console.log('🔍 Running continuous manual detection...');
        tryManualBarcodeDetection();
      }
    }, 3000); // Every 3 seconds
    
    return () => clearInterval(manualDetectionInterval);
  }, [isScanning, tryManualBarcodeDetection]);

  if (!isOpen) return null;

  return (
    <div style={{
      position: 'fixed',
      bottom: 0,
      left: 0,
      right: 0,
      backgroundColor: 'rgba(255, 255, 255, 0.95)',
      backdropFilter: 'blur(12px)',
      borderTop: '1px solid rgba(0, 0, 0, 0.1)',
      borderTopLeftRadius: '16px',
      borderTopRightRadius: '16px',
      padding: '24px',
      maxHeight: '80vh',
      overflowY: 'auto',
      zIndex: 1000,
      transform: 'translateY(0)',
      transition: 'transform 0.3s ease-out'
    }}>
      <div style={{
        display: 'flex',
        justifyContent: 'space-between',
        alignItems: 'center',
        marginBottom: '24px'
      }}>
        <h2 style={{ margin: 0, fontSize: '12px', fontWeight: '600' }}>Product Information</h2>
        <button
          onClick={onClose}
          style={{
            background: 'none',
            border: 'none',
                            fontSize: '12px',
            cursor: 'pointer',
            color: ACCENT_GRAY
          }}
        >
          ×
        </button>
      </div>

      <div style={{ display: 'flex', flexDirection: 'column', gap: '16px' }}>
        {/* Video Thumbnail Selector */}
        {videoUrl && (
          <div>
            <div
              onClick={() => handleThumbnailSelect(0)}
              style={{
                width: '120px',
                height: '213px', // 120 * 16/9 = 213.33... ≈ 213
                borderRadius: '8px',
                overflow: 'hidden',
                cursor: 'pointer',
                border: '2px solid #ccc',
                backgroundColor: '#f5f5f5',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                position: 'relative'
              }}
            >
              {thumbnails.length > 0 ? (
                <img
                  src={thumbnails[selectedThumbnail]}
                  alt="Product thumbnail"
                  style={{
                    width: '100%',
                    height: '100%',
                    objectFit: 'cover'
                  }}
                />
              ) : (
                <div style={{ textAlign: 'center', color: '#666' }}>
                  <div style={{ display: 'flex', justifyContent: 'center', marginBottom: '4px' }}>
                    <Image
                      size={24}
                      style={{
                        color: '#666'
                      }}
                    />
                  </div>
                  <div style={{ fontSize: '12px' }}>Tap to select frame</div>
                </div>
              )}
            </div>
            
            {/* Video Scrubber Slider */}
            {videoDuration > 0 && (
              <div style={{ marginTop: '12px' }}>
                <input
                  type="range"
                  min="0"
                  max={videoDuration}
                  step="0.1"
                  value={currentTime}
                  onChange={handleSliderChange}
                  style={{
                    width: '100%',
                    height: '4px',
                    borderRadius: '2px',
                    background: 'linear-gradient(135deg, #f4a261 0%, #e76f51 50%, #f4a261 100%)',
                    backgroundSize: '200% 200%',
                    animation: 'gradientShift 3s ease infinite',
                    outline: 'none',
                    cursor: 'pointer',
                    WebkitAppearance: 'none',
                    MozAppearance: 'none'
                  }}
                />
                <p style={{ fontSize: '12px', color: '#666', margin: '8px 0 0 0', textAlign: 'left' }}>
                  Slide to select the image for your product
                </p>
              </div>
            )}
          </div>
        )}

        {/* Hidden canvas for generating thumbnails */}
        <canvas ref={canvasRef} style={{ display: 'none' }} />

        <div>
          <label htmlFor="name" style={{ display: 'block', marginBottom: '4px', fontWeight: '500' }}>Name *</label>
          <input
            id="name"
            type="text"
            value={productInfo.name || ''}
            onChange={(e) => onUpdateProductInfo({ ...productInfo, name: e.target.value })}
            placeholder="Enter product name"
            style={{
              width: '100%',
              padding: '8px 12px',
              border: `1px solid ${errors.name ? ERROR_RED : '#ccc'}`,
              borderRadius: '8px',
              fontSize: '12px'
            }}
          />
          {errors.name && <p style={{ color: ERROR_RED, fontSize: '12px', margin: '4px 0 0 0' }}>{errors.name}</p>}
        </div>

        <div>
          <label htmlFor="description" style={{ display: 'block', marginBottom: '4px', fontWeight: '500' }}>Description</label>
          <textarea
            id="description"
            value={productInfo.description || ''}
            onChange={(e) => onUpdateProductInfo({ ...productInfo, description: e.target.value })}
            placeholder="Write a brief description"
            rows={3}
            style={{
              width: '100%',
              padding: '8px 12px',
              border: '1px solid #ccc',
              borderRadius: '8px',
              fontSize: '12px',
              resize: 'vertical'
            }}
          />
        </div>

        <div>
          <VariantEditor value={variants} onChange={setVariants} />
        </div>

        {/* Pricing & Inventory Section */}
        <div>
          {/* Price */}
          <div style={{ marginBottom: '16px' }}>
            <label htmlFor="price" style={{ display: 'block', marginBottom: '4px', fontWeight: '500' }}>Price ($) *</label>
            <input
              id="price"
              type="number"
              min="0"
              step="0.01"
              value={productInfo.price || ''}
              onChange={(e) => onUpdateProductInfo({ ...productInfo, price: e.target.value })}
              placeholder="0.00"
              style={{
                width: '100%',
                                padding: '8px 12px',
                border: `1px solid ${errors.price ? ERROR_RED : '#ccc'}`,
                borderRadius: '8px',
                fontSize: '12px'
            }}
          />
            {errors.price && (
              <p style={{ color: ERROR_RED, fontSize: '12px', margin: '4px 0 0 0' }}>{errors.price}</p>
            )}
          </div>

          {/* Cost */}
          <div style={{ marginBottom: '16px' }}>
            <label htmlFor="cost" style={{ display: 'block', marginBottom: '4px', fontWeight: '500' }}>Cost per item ($)</label>
            <input
              id="cost"
              type="number"
              min="0"
              step="0.01"
              value={productInfo.cost || ''}
              onChange={(e) => onUpdateProductInfo({ ...productInfo, cost: e.target.value })}
              placeholder="0.00"
              style={{
                width: '100%',
                                padding: '8px 12px',
                border: '1px solid #ccc',
                borderRadius: '8px',
                fontSize: '12px'
              }}
            />
          </div>

          {/* Profit Margin */}
          <div style={{ marginBottom: '16px' }}>
            <label style={{ display: 'block', marginBottom: '4px', fontWeight: '500' }}>Profit Margin (%)</label>
            <div style={{
              width: '100%',
                              padding: '8px 12px',
                border: '1px solid #e5e7eb',
                borderRadius: '8px',
                backgroundColor: '#f9fafb',
                color: '#6b7280',
                fontSize: '12px'
            }}>
              {(() => {
                if (!productInfo.price || !productInfo.cost || isNaN(productInfo.price) || isNaN(productInfo.cost) || Number(productInfo.price) === 0) return '--';
                const margin = Math.max(0, (((Number(productInfo.price) - Number(productInfo.cost)) / Number(productInfo.price)) * 100).toFixed(1));
                return margin + '%';
              })()}
            </div>
          </div>
        </div>

        <div>
          <label htmlFor="ingredients" style={{ display: 'block', marginBottom: '4px', fontWeight: '500' }}>Ingredients</label>
          
          {/* Ingredient Search Input */}
          <div style={{ position: 'relative', marginBottom: '12px' }}>
            <input
              id="ingredients"
              type="text"
              value={ingredientSearch}
              onChange={(e) => setIngredientSearch(e.target.value)}
              placeholder="Search ingredients (type at least 3 characters)"
              style={{
                width: '100%',
                padding: '8px 12px',
                paddingLeft: '40px',
                paddingRight: '50px',
                border: '1px solid #ccc',
                borderRadius: '8px',
                fontSize: '12px',
                backgroundColor: 'white'
              }}
            />
            <div style={{
              position: 'absolute',
              left: '12px',
              top: '50%',
              transform: 'translateY(-50%)',
              color: '#666',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center'
            }}>
              <Search
                size={20}
                style={{
                  color: '#666'
                }}
              />
            </div>
            <button
              type="button"
              onClick={startBarcodeScan}
              style={{
                position: 'absolute',
                right: '12px',
                top: '50%',
                transform: 'translateY(-50%)',
                background: 'none',
                border: 'none',
                cursor: 'pointer',
                padding: '4px',
                borderRadius: '4px',
                color: '#666',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center'
              }}
              title="Scan barcode"
              aria-label="Scan barcode"
            >
              <svg width="20" height="20" viewBox="0 0 24 24" fill="currentColor">
                <path d="M3 3h6v6H3V3zm2 2v2h2V5H5zm8-2h6v6h-6V3zm2 2v2h2V5h-2zM3 11h6v6H3v-6zm2 2v2h2v-2H5zm8 2h6v6h-6v-6zm2 2v2h2v-2h-2z"/>
                <path d="M9 7h2v2H9V7zm4 0h2v2h-2V7zM9 15h2v2H9v-2zm4 0h2v2h-2v-2z"/>
              </svg>
            </button>
          </div>

          {/* Search Results Dropdown */}
          {ingredientResults.length > 0 && (
            <div style={{
              border: '1px solid #ccc',
              borderRadius: '8px',
              backgroundColor: 'white',
              maxHeight: '200px',
              overflowY: 'auto',
              marginBottom: '12px',
              boxShadow: '0 2px 8px rgba(0,0,0,0.1)'
            }}>
              {ingredientResults.map((ingredient) => (
                <div
                  key={ingredient.id}
                  onClick={() => addIngredient(ingredient)}
                  style={{
                    padding: '12px',
                    cursor: 'pointer',
                    borderBottom: '1px solid #f0f0f0',
                    display: 'flex',
                    alignItems: 'center',
                    gap: '8px'
                  }}
                  onMouseEnter={(e) => e.target.style.backgroundColor = '#f5f5f5'}
                  onMouseLeave={(e) => e.target.style.backgroundColor = 'white'}
                >
                  <div style={{ flex: 1, display: 'flex', flexDirection: 'column', gap: '4px' }}>
                    <span style={{ fontWeight: '500' }}>{ingredient.name}</span>
                    <div style={{ display: 'flex', flexDirection: 'column', gap: '2px' }}>
                      {ingredient.brand && ingredient.brand !== 'Unknown' && (
                        <span style={{
                          fontSize: '11px',
                          color: '#666',
                          fontStyle: 'italic'
                        }}>
                          {ingredient.brand}
                        </span>
                      )}
                      {ingredient.calories > 0 && (
                        <span style={{
                          fontSize: '11px',
                          color: '#666'
                        }}>
                          {Math.round(ingredient.calories)} cals per 100g
                        </span>
                      )}
                      {ingredient.productsCount > 0 && (
                        <span style={{
                          fontSize: '11px',
                          color: '#666',
                          fontStyle: 'italic'
                        }}>
                          Used in {ingredient.productsCount.toLocaleString()} products
                        </span>
                      )}
                    </div>
                  </div>
                  <div style={{ display: 'flex', gap: '4px' }}>
                    {ingredient.vegetarian && (
                      <span style={{
                        fontSize: '12px',
                        padding: '2px 6px',
                        backgroundColor: '#e8f5e8',
                        color: '#2d5a2d',
                        borderRadius: '4px'
                      }}>Vegetarian</span>
                    )}
                    {ingredient.vegan && (
                      <span style={{
                        fontSize: '12px',
                        padding: '2px 6px',
                        backgroundColor: '#e8f5e8',
                        color: '#2d5a2d',
                        borderRadius: '4px'
                      }}>Vegan</span>
                    )}
                  </div>
                </div>
              ))}
            </div>
          )}

          {/* Error Message */}
          {searchError && (
            <div style={{
              color: '#d32f2f',
              fontSize: '12px',
              marginBottom: '12px'
            }}>
              {searchError}
            </div>
          )}

          {/* No Results Message */}
          {ingredientSearch.length >= 3 && ingredientResults.length === 0 && !isSearching && !searchError && (
            <div style={{
              color: '#666',
              fontSize: '12px',
              marginBottom: '12px'
            }}>
              No matching ingredients found. Try another term.
            </div>
          )}

          {/* Loading Indicator */}
          {isSearching && (
            <div style={{
              color: '#666',
              fontSize: '12px',
              marginBottom: '12px'
            }}>
              Searching ingredients...
            </div>
          )}

          {/* Selected Ingredients Chips */}
          {productInfo.ingredients.length > 0 && (
            <div style={{
              display: 'flex',
              flexWrap: 'wrap',
              gap: '8px',
              marginTop: '8px'
            }}>
              {productInfo.ingredients.map((ingredient) => (
                <div
                  key={ingredient.id}
                  style={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: '6px',
                    padding: '6px 12px',
                    backgroundColor: '#f5f5f5',
                    borderRadius: '20px',
                    fontSize: '12px',
                    color: '#333'
                  }}
                >
                  <span>{ingredient.name}</span>
                  <button
                    type="button"
                    onClick={() => removeIngredient(ingredient.id)}
                    style={{
                      background: 'none',
                      border: 'none',
                      cursor: 'pointer',
                      fontSize: '12px',
                      color: '#666',
                      padding: '0',
                      width: '16px',
                      height: '16px',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center'
                    }}
                    aria-label={`Remove ${ingredient.name}`}
                  >
                    ×
                  </button>
                </div>
              ))}
            </div>
          )}
        </div>

        {/* Ingredients Warning */}
        <div style={{
          background: '#FFF3CD',
          border: '1px solid #FFEAA7',
          borderRadius: 6,
          padding: '8px 12px',
          marginBottom: '16px'
        }}>
          <div style={{
            display: 'flex',
            alignItems: 'flex-start',
            gap: '6px'
          }}>
            <span style={{
              color: '#856404',
              fontSize: '12px',
              fontWeight: 600
            }}>
              ⚠️
            </span>
            <div style={{
              fontSize: '11px',
              color: '#856404',
              lineHeight: '1.4'
            }}>
              <strong>Important:</strong> Please be accurate with your ingredients. Allergies can be life-threatening, and customers rely on this information for their safety. We will not display all ingredients to users (keeping your recipes safe), but we will showcase allergy warnings.
            </div>
          </div>
        </div>

        {/* Category and Sub-Category */}
        <div style={{ marginBottom: '24px' }}>
          <div style={{ marginBottom: '12px' }}>
            <label htmlFor="listingType" style={{ display: 'block', marginBottom: '4px', fontWeight: '500' }}>
              Category {taxonomyData?.listingType?.required && <span style={{ color: ERROR_RED }}>*</span>}
            </label>
            <select
              id="listingType"
              value={productInfo.listingType || ''}
              onChange={(e) => onUpdateProductInfo({ ...productInfo, listingType: e.target.value })}
              style={{
                width: '100%',
                padding: '8px 12px',
                border: '1px solid #ccc',
                borderRadius: '8px',
                fontSize: '12px',
                backgroundColor: 'white'
              }}
            >
              <option value="">Select a category</option>
              {taxonomyData?.listingType?.values?.map(item => (
                <option key={item.value} value={item.value}>{item.display}</option>
              ))}
            </select>
          </div>

          <div style={{ marginBottom: '12px' }}>
            <label htmlFor="itemCategory" style={{ display: 'block', marginBottom: '4px', fontWeight: '500' }}>
              Sub-Category {taxonomyData?.itemCategory?.required && <span style={{ color: ERROR_RED }}>*</span>}
            </label>
            <select
              id="itemCategory"
              value={productInfo.itemCategory || ''}
              onChange={(e) => onUpdateProductInfo({ ...productInfo, itemCategory: e.target.value })}
              style={{
                width: '100%',
                padding: '8px 12px',
                border: '1px solid #ccc',
                borderRadius: '8px',
                fontSize: '12px',
                backgroundColor: 'white'
              }}
            >
              <option value="">Select a sub-category</option>
              {taxonomyData?.itemCategory?.values?.map(item => (
                <option key={item.value} value={item.value}>{item.display}</option>
              ))}
            </select>
          </div>
        </div>

        {/* Dietary & Lifestyle Tags */}
        <div>
          <label style={{ display: 'block', marginBottom: '8px', fontWeight: '500' }}>
            Dietary & Lifestyle Tags
          </label>
          
          {!taxonomyData && (
            <div style={{ fontSize: '12px', color: '#f44336', marginBottom: '8px' }}>
              Taxonomy data not available. Please refresh the page.
            </div>
          )}
          
          {/* Cuisine */}
          {taxonomyData?.cuisine && (
            <div style={{ marginBottom: '12px' }}>
              <label htmlFor="cuisine" style={{ display: 'block', marginBottom: '4px', fontSize: '12px', color: '#666' }}>
                Cuisine
              </label>
              <select
                id="cuisine"
                value={productInfo.cuisine || ''}
                onChange={(e) => onUpdateProductInfo({ ...productInfo, cuisine: e.target.value })}
                style={{
                  width: '100%',
                  padding: '8px 12px',
                  border: '1px solid #ccc',
                  borderRadius: '6px',
                  fontSize: '12px',
                  backgroundColor: 'white'
                }}
              >
                <option value="">Select cuisine (optional)</option>
                {taxonomyData.cuisine.values?.map(item => (
                  <option key={item.value} value={item.value}>{item.display}</option>
                ))}
              </select>
            </div>
          )}

          {/* Course Type */}
          {taxonomyData?.courseType && (
            <div style={{ marginBottom: '12px' }}>
              <label htmlFor="courseType" style={{ display: 'block', marginBottom: '4px', fontSize: '12px', color: '#666' }}>
                Course Type
              </label>
              <select
                id="courseType"
                value={productInfo.courseType || ''}
                onChange={(e) => onUpdateProductInfo({ ...productInfo, courseType: e.target.value })}
                style={{
                  width: '100%',
                  padding: '8px 12px',
                  border: '1px solid #ccc',
                  borderRadius: '6px',
                  fontSize: '12px',
                  backgroundColor: 'white'
                }}
              >
                <option value="">Select course type (optional)</option>
                {taxonomyData.courseType.values?.map(item => (
                  <option key={item.value} value={item.value}>{item.display}</option>
                ))}
              </select>
            </div>
          )}

          {/* Dish Type */}
          {taxonomyData?.dishType && (
            <div style={{ marginBottom: '12px' }}>
              <label htmlFor="dishType" style={{ display: 'block', marginBottom: '4px', fontSize: '12px', color: '#666' }}>
                Dish Type
              </label>
              <select
                id="dishType"
                value={productInfo.dishType || ''}
                onChange={(e) => onUpdateProductInfo({ ...productInfo, dishType: e.target.value })}
                style={{
                  width: '100%',
                  padding: '8px 12px',
                  border: '1px solid #ccc',
                  borderRadius: '6px',
                  fontSize: '12px',
                  backgroundColor: 'white'
                }}
              >
                <option value="">Select dish type (optional)</option>
                {taxonomyData.dishType.values?.map(item => (
                  <option key={item.value} value={item.value}>{item.display}</option>
                ))}
              </select>
            </div>
          )}

          {/* Dietary Tags (Search Dropdown) */}
          {taxonomyData?.dietaryTags && (
            <div style={{ marginBottom: '12px' }}>
              <label style={{ display: 'block', marginBottom: '8px', fontSize: '12px', color: '#666' }}>
                Dietary Tags
              </label>
              
              {/* Search Input */}
              <div style={{ position: 'relative', marginBottom: '8px' }}>
                <input
                  type="text"
                  placeholder="Search dietary tags..."
                  value={dietaryTagSearch}
                  onChange={(e) => setDietaryTagSearch(e.target.value)}
                  style={{
                    width: '100%',
                    padding: '8px 12px',
                    paddingLeft: '40px',
                    border: '1px solid #ccc',
                    borderRadius: '8px',
                    fontSize: '12px',
                    backgroundColor: 'white'
                  }}
                />
                <div style={{
                  position: 'absolute',
                  left: '12px',
                  top: '50%',
                  transform: 'translateY(-50%)',
                  color: '#666',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center'
                }}>
                  <Search
                    size={20}
                    style={{
                      color: '#666'
                    }}
                  />
                </div>
              </div>

              {/* Search Results Dropdown */}
              {dietaryTagResults.length > 0 && (
                <div style={{
                  border: '1px solid #ccc',
                  borderRadius: '8px',
                  backgroundColor: 'white',
                  maxHeight: '200px',
                  overflowY: 'auto',
                  marginBottom: '12px',
                  boxShadow: '0 2px 8px rgba(0,0,0,0.1)'
                }}>
                  {dietaryTagResults.map((tag) => (
                    <div
                      key={tag.value}
                      onClick={() => addDietaryTag(tag)}
                      style={{
                        padding: '12px',
                        cursor: 'pointer',
                        borderBottom: '1px solid #f0f0f0',
                        display: 'flex',
                        alignItems: 'center',
                        gap: '8px'
                      }}
                      onMouseEnter={(e) => e.target.style.backgroundColor = '#f5f5f5'}
                      onMouseLeave={(e) => e.target.style.backgroundColor = 'white'}
                    >
                      <div style={{ flex: 1 }}>
                        <span style={{ fontWeight: '500' }}>{tag.display || tag.value}</span>
                      </div>
                    </div>
                  ))}
                </div>
              )}

              {/* Selected Dietary Tags Chips */}
              {productInfo.dietaryTags?.length > 0 && (
                <div style={{
                  display: 'flex',
                  flexWrap: 'wrap',
                  gap: '8px',
                  marginTop: '8px'
                }}>
                  {productInfo.dietaryTags.map((tagValue) => {
                    const tag = taxonomyData.dietaryTags.values.find(t => t.value === tagValue);
                    return (
                      <div
                        key={tagValue}
                        style={{
                          display: 'flex',
                          alignItems: 'center',
                          gap: '6px',
                          padding: '6px 12px',
                          backgroundColor: '#f5f5f5',
                          borderRadius: '20px',
                          fontSize: '12px',
                          color: '#333'
                        }}
                      >
                        <span>{tag?.display || tagValue}</span>
                        <button
                          type="button"
                          onClick={() => removeDietaryTag(tagValue)}
                          style={{
                            background: 'none',
                            border: 'none',
                            cursor: 'pointer',
                            fontSize: '12px',
                            color: '#666',
                            padding: '0',
                            width: '16px',
                            height: '16px',
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center'
                          }}
                          aria-label={`Remove ${tag?.display || tagValue}`}
                        >
                          ×
                        </button>
                      </div>
                    );
                  })}
                </div>
              )}
            </div>
          )}

          {/* Barcode Scanning Modal */}
          {isScanning && (
            <div style={{
              position: 'fixed',
              top: 0,
              left: 0,
              right: 0,
              bottom: 0,
              backgroundColor: 'rgba(0, 0, 0, 0.8)',
              zIndex: 1000,
              display: 'flex',
              flexDirection: 'column',
              alignItems: 'center',
              justifyContent: 'center',
              padding: '20px'
            }}>
              <div style={{
                backgroundColor: 'white',
                borderRadius: '12px',
                padding: '20px',
                maxWidth: '90vw',
                maxHeight: '90vh',
                display: 'flex',
                flexDirection: 'column',
                alignItems: 'center',
                gap: '16px'
              }}>
                <h3 style={{ margin: 0, fontSize: '12px', fontWeight: '600' }}>
                  Scan Barcode
                </h3>
                
                {/* Section 1: Large Camera View */}
                <div style={{
                  position: 'relative',
                  width: '100%',
                  height: '240px',
                  borderRadius: '16px',
                  overflow: 'hidden',
                  backgroundColor: '#f8f9fa',
                  border: '1px solid #e9ecef'
                }}>
                  <video
                    ref={videoRef}
                    style={{
                      width: '100%',
                      height: '100%',
                      objectFit: 'cover'
                    }}
                    autoPlay
                    playsInline
                    muted
                    onLoadedMetadata={() => {
                      if (cameraStream) {
                        startBarcodeDetection(cameraStream);
                      }
                    }}
                  />
                  

                  
                  {/* Scanning overlay with light brown border */}
                  <div style={{
                    position: 'absolute',
                    top: '50%',
                    left: '50%',
                    transform: 'translate(-50%, -50%)',
                    width: '80%',
                    height: '60%',
                    border: '2px solid #d4a574',
                    borderRadius: '12px',
                    pointerEvents: 'none',
                    backgroundColor: 'rgba(212, 165, 116, 0.1)'
                  }} />


                  

                </div>


                
                <div style={{
                  fontSize: '12px',
                  color: '#666',
                  textAlign: 'center',
                  maxWidth: '280px',
                  marginBottom: '8px'
                }}>
                  {isScanning ? (
                    barcodeCanvasRef.current && barcodeCanvasRef.current.width > 0 ? 
                    'Scanning continuously - Align barcode in green box' : 
                    'Initializing camera...'
                  ) : barcodeData ? 
                    (scanError ? scanError : 'Barcode detected successfully!') : 
                    'Camera ready'}
                </div>

                <div style={{
                  display: 'flex',
                  gap: '12px',
                  marginTop: '8px',
                  justifyContent: 'center',
                  flexWrap: 'wrap'
                }}>

                  

                  

                    
                {/* Section 2: Manual Barcode Entry */}
                <div style={{ 
                  display: 'flex', 
                  gap: '12px', 
                  alignItems: 'center', 
                  width: '100%'
                }}>
                  <span style={{ 
                    color: '#666', 
                    fontSize: '12px',
                    fontWeight: '500'
                  }}>
                    or
                  </span>
                  <input
                    type="text"
                    placeholder="Enter barcode manually"
                    maxLength="20"
                    style={{
                      flex: 1,
                      padding: '8px 16px',
                      border: '1px solid #ddd',
                      borderRadius: '12px',
                      fontSize: '12px',
                      fontFamily: 'monospace',
                      textAlign: 'center',
                      backgroundColor: 'white'
                    }}
                    onChange={(e) => {
                      // Only allow numbers
                      const value = e.target.value.replace(/[^0-9]/g, '');
                      e.target.value = value;
                    }}
                    onKeyPress={(e) => {
                      if (e.key === 'Enter') {
                        handleManualBarcodeSubmit(e.target.value);
                        e.target.value = '';
                      }
                    }}
                  />
                  <button
                    onClick={() => {
                      const input = document.querySelector('input[placeholder="Enter barcode manually"]');
                      if (input) {
                        handleManualBarcodeSubmit(input.value);
                        input.value = '';
                      }
                    }}
                    style={{
                      padding: '12px 20px',
                      backgroundColor: 'white',
                      color: '#333',
                      border: '1px solid #ddd',
                      borderRadius: '12px',
                      fontSize: '12px',
                      fontWeight: '500',
                      cursor: 'pointer',
                      whiteSpace: 'nowrap'
                    }}
                  >
                    Look up
                  </button>
                </div>



                {/* Section 3: Product Display */}
                {detectedProduct && (
                  <div style={{
                    backgroundColor: 'white',
                    border: '1px solid #e9ecef',
                    borderRadius: '12px',
                    padding: '16px',
                    width: '100%',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'space-between'
                  }}>
                    <div style={{ 
                      fontSize: '12px',
                      fontWeight: '500',
                      color: '#333',
                      flex: 1
                    }}>
                      {detectedProduct.product_name || 'Unknown Product'}
                    </div>
                    <button
                      onClick={() => {
                        setDetectedProduct(null);
                        setBarcodeData(null);
                        setScanError('Ready for next scan. Try a different barcode.');
                      }}
                      style={{
                        background: 'none',
                        border: 'none',
                        cursor: 'pointer',
                        padding: '8px',
                        color: '#666',
                        fontSize: '12px'
                      }}
                      title="Remove product"
                    >
                      🗑️
                    </button>
                  </div>
                )}

                {/* Error Messages */}
                {scanError && (
                  <div style={{
                    color: COLORS.ERROR_RED,
                    fontSize: '12px',
                    textAlign: 'center',
                    width: '100%',
                    fontWeight: '500'
                  }}>
                    {scanError}
                  </div>
                )}

                {/* Section 4: Action Buttons */}
                <div style={{ 
                  display: 'flex', 
                  gap: '12px', 
                  width: '100%',
                  marginTop: '8px'
                }}>
                  <button
                    onClick={stopBarcodeScan}
                    style={{
                      flex: 1,
                      padding: '14px 20px',
                      backgroundColor: 'white',
                      color: '#333',
                      border: '1px solid #ddd',
                      borderRadius: '12px',
                      fontSize: '12px',
                      fontWeight: '500',
                      cursor: 'pointer'
                    }}
                  >
                    Cancel
                  </button>
                  <button
                    onClick={detectedProduct ? addDetectedProductToIngredients : () => {}}
                    disabled={!detectedProduct}
                    style={{
                      flex: 1,
                      padding: '14px 20px',
                      backgroundColor: detectedProduct ? COLORS.PRIMARY_BROWN : '#ccc',
                      color: 'white',
                      border: 'none',
                      borderRadius: '12px',
                      fontSize: '12px',
                      fontWeight: '500',
                      cursor: detectedProduct ? 'pointer' : 'not-allowed'
                    }}
                  >
                    Add Products
                  </button>
                </div>
                </div>
              </div>
            </div>
          )}
        </div>




      </div>

      {/* Availability Information Card */}
      <div style={{
        marginTop: '24px',
        padding: '16px',
        backgroundColor: '#E3F2FD',
        borderRadius: '8px',
        border: '1px solid #BBDEFB'
      }}>
        <div style={{
          display: 'flex',
          alignItems: 'flex-start',
          gap: '8px'
        }}>
          <span style={{
            color: '#1976D2',
            fontSize: '16px',
            fontWeight: 600
          }}>
            ℹ️
          </span>
          <div style={{
            fontSize: '12px',
            color: '#1976D2',
            lineHeight: '1.5'
          }}>
            <strong>Availability Management:</strong> You'll be able to set up detailed availability schedules, inventory levels, and delivery/pickup options after creating your product on the products page. This includes setting specific dates, time slots, and managing stock levels for each day.
          </div>
        </div>
      </div>



      <div style={{
        display: 'flex',
        gap: '12px',
        marginTop: '24px'
      }}>
        <button
          onClick={handleSave}
          style={{
            flex: 1,
            padding: '12px',
            background: 'linear-gradient(135deg, #f4a261 0%, #e76f51 50%, #f4a261 100%)',
            backgroundSize: '200% 200%',
            animation: 'gradientShift 3s ease infinite',
            color: 'white',
            border: 'none',
            borderRadius: '8px',
            fontSize: '12px',
            fontWeight: '600',
            cursor: 'pointer'
          }}
        >
          Save
        </button>
      </div>
    </div>
  );
};

export default function CreateProductVideo() {
  const { currentUser } = useAuth();
  const navigate = useNavigate();
  const fileInputRef = useRef(null);
  const mainVideoRef = useRef(null);

  const [videoFile, setVideoFile] = useState(null);
  const [videoUrl, setVideoUrl] = useState(null);
  const [uploadProgress, setUploadProgress] = useState(0);
  const [isUploading, setIsUploading] = useState(false);
  const [uploadError, setUploadError] = useState(null);
  const [showProductDrawer, setShowProductDrawer] = useState(false);
  const [productInfo, setProductInfo] = useState({
    name: '',
    description: '',
    ingredients: [],
    listingType: '',      // Category
    itemCategory: '',     // Sub-Category
    cuisine: '',          // Dietary & Lifestyle Tags
    dietaryTags: [],      // Dietary & Lifestyle Tags (multi-select)
    courseType: '',       // Dietary & Lifestyle Tags
    dishType: '',         // Dietary & Lifestyle Tags
    price: '',
    cost: '',
    startDate: '',
    endDate: '',
    availableDays: [],
    dailyInventory: {}, // New field for daily inventory
    imageUrl: '',
    status: false  // Add status field with default false
  });
  const [variants, setVariants] = useState([]);

  // Taxonomy data state
  const [taxonomyData, setTaxonomyData] = useState(null);
  const [taxonomyLoading, setTaxonomyLoading] = useState(true);

  // Load taxonomy data on component mount
  useEffect(() => {
    const loadTaxonomyData = async () => {
      try {
        console.log('🔄 Loading taxonomy data...');
        const data = await getTaxonomyForProductForm();
        console.log('📊 Taxonomy data loaded:', data);
        console.log('🏷️ Dietary tags:', data?.dietaryTags);
        
        // If no data from Firebase, use default values as fallback
        if (!data || Object.keys(data).length === 0) {
          console.log('⚠️ No taxonomy data from Firebase, using defaults...');
          const defaultData = {
            listingType: {
              name: 'Listing Type',
              description: 'Product category type',
              required: true,
              singleValue: true,
              defaultValue: 'meal_prep',
              values: [
                { value: 'meal_prep', display: 'Meal Prep' },
                { value: 'ready_now', display: 'Ready Now' },
                { value: 'order_ahead', display: 'Order Ahead' },
                { value: 'in_person_chef', display: 'In-Person Chef' },
                { value: 'bake_at_home', display: 'Bake at Home' }
              ]
            },
            itemCategory: {
              name: 'Item Category',
              description: 'Subcategory of item',
              required: true,
              singleValue: true,
              defaultValue: 'entree',
              values: [
                { value: 'baked_goods', display: 'Baked Goods' },
                { value: 'entree', display: 'Entree' },
                { value: 'side', display: 'Side Dish' },
                { value: 'course', display: 'Course' },
                { value: 'preserves', display: 'Preserves' },
                { value: 'candy', display: 'Candy' }
              ]
            },
            cuisine: {
              name: 'Cuisine',
              description: 'Style/region',
              required: false,
              singleValue: true,
              defaultValue: null,
              values: [
                { value: 'american', display: 'American' },
                { value: 'italian', display: 'Italian' },
                { value: 'mexican', display: 'Mexican' },
                { value: 'chinese', display: 'Chinese' },
                { value: 'japanese', display: 'Japanese' },
                { value: 'thai', display: 'Thai' },
                { value: 'indian', display: 'Indian' },
                { value: 'mediterranean', display: 'Mediterranean' }
              ]
            },
            dietaryTags: {
              name: 'Dietary Tags',
              description: 'Dietary & lifestyle tags',
              required: false,
              singleValue: false,
              defaultValue: [],
              values: [
                { value: 'vegan', display: 'Vegan' },
                { value: 'vegetarian', display: 'Vegetarian' },
                { value: 'gluten_free', display: 'Gluten Free' },
                { value: 'dairy_free', display: 'Dairy Free' },
                { value: 'keto', display: 'Keto' },
                { value: 'paleo', display: 'Paleo' },
                { value: 'low_carb', display: 'Low Carb' },
                { value: 'high_protein', display: 'High Protein' },
                { value: 'halal_friendly', display: 'Halal Friendly' },
                { value: 'kosher_style', display: 'Kosher Style' }
              ]
            },
            courseType: {
              name: 'Course Type',
              description: 'Meal/course classification',
              required: false,
              singleValue: true,
              defaultValue: null,
              values: [
                { value: 'breakfast', display: 'Breakfast' },
                { value: 'lunch', display: 'Lunch' },
                { value: 'dinner', display: 'Dinner' },
                { value: 'appetizer', display: 'Appetizer' },
                { value: 'dessert', display: 'Dessert' },
                { value: 'snack', display: 'Snack' }
              ]
            },
            dishType: {
              name: 'Dish Type',
              description: 'Dish preparation/style',
              required: false,
              singleValue: true,
              defaultValue: null,
              values: [
                { value: 'pizza_flatbread', display: 'Pizza & Flatbread' },
                { value: 'pasta_noodles', display: 'Pasta & Noodles' },
                { value: 'salad', display: 'Salad' },
                { value: 'soup_stew', display: 'Soup & Stew' },
                { value: 'curry', display: 'Curry' },
                { value: 'cake', display: 'Cake' },
                { value: 'bread', display: 'Bread' }
              ]
            }
          };
          setTaxonomyData(defaultData);
        } else {
          setTaxonomyData(data);
        }
      } catch (error) {
        console.error('❌ Error loading taxonomy data:', error);
        // Use default values on error
        console.log('⚠️ Using default taxonomy data due to error...');
        // ... same default data as above
      } finally {
        setTaxonomyLoading(false);
      }
    };

    loadTaxonomyData();
  }, []);

  const handleFileSelect = (event) => {
    const file = event.target.files[0];
    if (!file) return;

    // Validate file type
    const allowedTypes = ['video/mp4', 'video/mov', 'video/webm'];
    if (!allowedTypes.includes(file.type)) {
      setUploadError('Please select a valid video file (MP4, MOV, or WebM)');
      return;
    }

    // Validate file size (100MB)
    if (file.size > 100 * 1024 * 1024) {
      setUploadError('File size must be less than 100MB');
      return;
    }

    setVideoFile(file);
    setUploadError(null);
    
    // Create preview URL
    const previewUrl = URL.createObjectURL(file);
    setVideoUrl(previewUrl);
  };

  const uploadVideo = async () => {
    if (!videoFile || !currentUser) return;

    setIsUploading(true);
    setUploadProgress(0);
    setUploadError(null);

    try {
      const storageRef = ref(storage, `videos/${currentUser.uid}/${Date.now()}_${videoFile.name}`);
      const uploadTask = uploadBytesResumable(storageRef, videoFile);

      uploadTask.on('state_changed',
        (snapshot) => {
          const progress = (snapshot.bytesTransferred / snapshot.totalBytes) * 100;
          setUploadProgress(progress);
        },
        (error) => {
          setUploadError('Upload failed: ' + error.message);
          setIsUploading(false);
        },
        async () => {
          const downloadURL = await getDownloadURL(uploadTask.snapshot.ref);
          setVideoUrl(downloadURL);
          setIsUploading(false);
        }
      );
    } catch (error) {
      setUploadError('Upload failed: ' + error.message);
      setIsUploading(false);
    }
  };



  const updateProductInfo = (updates) => {
    console.log('Updating product info:', updates);
    setProductInfo({ ...productInfo, ...updates });
  };

  // Daily inventory handlers
  const handleDayToggle = (dayIndex) => {
    const newAvailableDays = productInfo.availableDays.includes(dayIndex)
      ? productInfo.availableDays.filter(d => d !== dayIndex)
      : [...productInfo.availableDays, dayIndex];
    
    const newDailyInventory = { ...productInfo.dailyInventory };
    if (!newAvailableDays.includes(dayIndex)) {
      // If removing a day, also remove its inventory
      delete newDailyInventory[dayIndex];
    } else {
      // If adding a day, initialize it with 0 inventory
      newDailyInventory[dayIndex] = 0;
    }
    
    setProductInfo({
      ...productInfo,
      availableDays: newAvailableDays,
      dailyInventory: newDailyInventory
    });
  };

  const handleDailyInventoryChange = (dayIndex, value) => {
    setProductInfo({
      ...productInfo,
      dailyInventory: {
        ...productInfo.dailyInventory,
        [dayIndex]: Math.max(0, parseInt(value) || 0)
      }
    });
  };





  const publishProduct = async () => {
    if (!currentUser || !productInfo.name) {
      showError('Please fill in the product name and upload a video');
      return;
    }

    if (!videoUrl) {
      showError('Please upload a video before publishing');
      return;
    }

    try {
      let finalVideoUrl = videoUrl;
      
      // If videoUrl is a blob URL, upload it to Firebase Storage first
      if (videoUrl.startsWith('blob:')) {
        if (!videoFile) {
          showError('Video file not found. Please try uploading the video again.');
          return;
        }
        
        // Show upload progress
        setIsUploading(true);
        setUploadProgress(0);
        
        try {
          const storageRef = ref(storage, `videos/${currentUser.uid}/${Date.now()}_${videoFile.name}`);
          const uploadTask = uploadBytesResumable(storageRef, videoFile);

          // Wait for upload to complete
          await new Promise((resolve, reject) => {
            uploadTask.on('state_changed',
              (snapshot) => {
                const progress = (snapshot.bytesTransferred / snapshot.totalBytes) * 100;
                setUploadProgress(progress);
              },
              (error) => {
                reject(error);
              },
              async () => {
                try {
                  const downloadURL = await getDownloadURL(uploadTask.snapshot.ref);
                  finalVideoUrl = downloadURL;
                  resolve();
                } catch (error) {
                  reject(error);
                }
              }
            );
          });
          
          setIsUploading(false);
          setUploadProgress(0);
        } catch (uploadError) {
          setIsUploading(false);
          setUploadProgress(0);
          showError('Failed to upload video: ' + uploadError.message);
          return;
        }
      }

      const productData = {
        videoUrl: finalVideoUrl,
        ...productInfo,
        variants,
        sellerId: currentUser.uid,
        status: productInfo.status  // Use dynamic status from user's choice
        // Note: createdAt and updatedAt will be automatically set by addProduct
        // Note: inventory schema will be automatically generated by addProduct
      };

      console.log('Saving product data:', productData);
      const savedProduct = await addProduct(productData);
      console.log('Product saved successfully with new inventory schema:', savedProduct);
      productToasts.created(productInfo.name);
      // Navigate back to seller hub
      navigate('/seller-hub');
    } catch (error) {
      console.error('Error creating product:', error);
      showError('Error creating product: ' + error.message);
    }
  };

  const handleCancel = () => {
    // For now, keep the confirm but we could enhance this with a custom modal later
    if (window.confirm('Are you sure you want to cancel? All progress will be lost.')) {
      // Navigate back to seller hub
      navigate('/seller-hub');
    }
  };

  return (
    <div style={{
      minHeight: '100vh',
      background: '#FFFFFF',
      position: 'relative',
      overflow: 'hidden',
      display: 'flex',
      justifyContent: 'center',
      alignItems: 'center'
    }}>
      <div 
        className="create-product-video-container"
        style={{
          position: 'relative',
          width: '100%',
          maxWidth: '450px',
          height: window.innerWidth <= 768 ? '100vh' : 'auto',
          maxHeight: window.innerWidth <= 768 ? '100vh' : '90vh',
          margin: '0 auto',
          background: '#000',
          borderRadius: window.innerWidth <= 768 ? '0' : '8px',
          overflow: 'hidden',
          aspectRatio: '9/16',
          display: 'flex',
          flexDirection: 'column'
        }}
      >
          {!videoUrl ? (
            <div style={{
              display: 'flex',
              flexDirection: 'column',
              alignItems: 'center',
              justifyContent: 'center',
              height: '100%',
              color: 'white'
            }}>
              <button
                onClick={() => fileInputRef.current?.click()}
                style={{
                  width: '80px',
                  height: '80px',
                  borderRadius: '50%',
                  backgroundColor: 'rgba(255, 255, 255, 0.2)',
                  border: '2px solid white',
                  color: 'white',
                  fontSize: '12px',
                  cursor: 'pointer',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  marginBottom: '16px'
                }}
              >
                ↑
              </button>
              <p style={{ margin: 0, fontSize: '12px' }}>Click to upload video</p>
              <p style={{ margin: '8px 0 0 0', fontSize: '12px', opacity: 0.7 }}>
                MP4, MOV, WebM up to 100MB
              </p>
            </div>
          ) : (
            <>
              <video
                ref={mainVideoRef}
                src={videoUrl}
                style={{
                  width: '100%',
                  height: '100%',
                  objectFit: 'cover'
                }}
                autoPlay
                loop
                muted
                playsInline
              />
              

              
              {/* CSS for proper video sizing on web view */}
              <style>{`
                @media (min-width: 769px) {
                  .create-product-video-container > div {
                    height: auto !important;
                    max-height: 90vh !important;
                  }
                  .create-product-video-container video {
                    height: auto !important;
                    min-height: auto !important;
                  }
                }
                
                @keyframes spin {
                  0% { transform: rotate(0deg); }
                  100% { transform: rotate(360deg); }
                }
                
                @keyframes gradientShift {
                  0% { background-position: 0% 50%; }
                  50% { background-position: 100% 50%; }
                  100% { background-position: 0% 50%; }
                }
                
                /* Enhanced slider styling for better gradient visibility */
                input[type="range"] {
                  -webkit-appearance: none;
                  -moz-appearance: none;
                  appearance: none;
                }
                
                input[type="range"]::-webkit-slider-track {
                  -webkit-appearance: none;
                  background: linear-gradient(135deg, #f4a261 0%, #e76f51 50%, #f4a261 100%);
                  background-size: 200% 200%;
                  animation: gradientShift 3s ease infinite;
                  height: 4px;
                  border-radius: 2px;
                }
                
                input[type="range"]::-webkit-slider-thumb {
                  -webkit-appearance: none;
                  appearance: none;
                  width: 16px;
                  height: 16px;
                  border-radius: 50%;
                  background: linear-gradient(135deg, #f4a261 0%, #e76f51 50%, #f4a261 100%);
                  background-size: 200% 200%;
                  animation: gradientShift 3s ease infinite;
                  cursor: pointer;
                  border: 2px solid white;
                  box-shadow: 0 2px 4px rgba(0,0,0,0.2);
                }
                
                input[type="range"]::-moz-range-track {
                  background: linear-gradient(135deg, #f4a261 0%, #e76f51 50%, #f4a261 100%);
                  background-size: 200% 200%;
                  animation: gradientShift 3s ease infinite;
                  height: 4px;
                  border-radius: 2px;
                  border: none;
                }
                
                input[type="range"]::-moz-range-thumb {
                  width: 16px;
                  height: 16px;
                  border-radius: 50%;
                  background: linear-gradient(135deg, #f4a261 0%, #e76f51 50%, #f4a261 100%);
                  background-size: 200% 200%;
                  animation: gradientShift 3s ease infinite;
                  cursor: pointer;
                  border: 2px solid white;
                  box-shadow: 0 2px 4px rgba(0,0,0,0.2);
                }
              `}</style>
              




              {/* Cancel Button - Inside Video */}
              <button
                onClick={handleCancel}
                style={{
                  position: 'absolute',
                  bottom: '16px',
                  left: '16px',
                  width: '40px',
                  height: '40px',
                  backgroundColor: 'transparent',
                  color: 'white',
                  border: 'none',
                  cursor: 'pointer',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  zIndex: 20
                }}
                title="Cancel"
              >
                <CloseSquare set="regular" size={24} primaryColor="white" />
              </button>



              {/* Product Info Button - Inside Video */}
              <button
                onClick={() => setShowProductDrawer(true)}
                style={{
                  position: 'absolute',
                  bottom: '64px',
                  right: '16px',
                  width: '40px',
                  height: '40px',
                  backgroundColor: 'transparent',
                  color: 'white',
                  border: 'none',
                  cursor: 'pointer',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  zIndex: 20,
                  fontSize: '12px',
                  fontWeight: 'bold'
                }}
                title="Product Info"
              >
                ...
              </button>

              {/* Save Button - Inside Video */}
              <button
                onClick={publishProduct}
                disabled={isUploading}
                style={{
                  position: 'absolute',
                  bottom: '16px',
                  right: '16px',
                  width: '40px',
                  height: '40px',
                  backgroundColor: 'transparent',
                  color: 'white',
                  border: 'none',
                  cursor: isUploading ? 'not-allowed' : 'pointer',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  zIndex: 20,
                  opacity: isUploading ? 0.6 : 1
                }}
                title={isUploading ? 'Uploading...' : 'Save'}
              >
                {isUploading ? (
                  <div style={{
                    width: '20px',
                    height: '20px',
                    border: '2px solid #fff',
                    borderTop: '2px solid transparent',
                    borderRadius: '50%',
                    animation: 'spin 1s linear infinite'
                  }} />
                ) : (
                  <TickSquare set="regular" size={24} primaryColor="white" />
                )}
              </button>
            </>
          )}

        </div>

      {/* Upload Progress - Centered on Video */}
      {isUploading && (
        <div style={{
          position: 'absolute',
          top: '50%',
          left: '50%',
          transform: 'translate(-50%, -50%)',
          zIndex: 1000,
          background: 'rgba(0, 0, 0, 0.8)',
          borderRadius: '16px',
          padding: '24px',
          textAlign: 'center',
          minWidth: '200px'
        }}>
          <div style={{
            color: 'white',
            fontSize: '12px',
            fontWeight: '600',
            marginBottom: '16px',
            fontFamily: 'Sora, sans-serif'
          }}>
            Uploading
          </div>
          <div style={{
            width: '100%',
            height: '8px',
            backgroundColor: 'rgba(255, 255, 255, 0.2)',
            borderRadius: '4px',
            overflow: 'hidden',
            marginBottom: '8px'
          }}>
            <div style={{
              width: `${uploadProgress}%`,
              height: '100%',
              background: 'linear-gradient(135deg, #f4a261 0%, #e76f51 50%, #f4a261 100%)',
              transition: 'width 0.3s ease',
              borderRadius: '4px'
            }} />
          </div>
          <div style={{
            color: 'rgba(255, 255, 255, 0.8)',
            fontSize: '12px',
            fontFamily: 'Montserrat, sans-serif'
          }}>
            {Math.round(uploadProgress)}%
          </div>
        </div>
      )}

      {/* Upload Error */}
      {uploadError && (
        <div style={{
          marginBottom: '16px',
          padding: '12px',
          backgroundColor: '#FFEBEE',
          borderRadius: '8px',
          border: '1px solid #F44336',
          color: ERROR_RED
        }}>
          {uploadError}
        </div>
      )}

      {/* Action Buttons */}
      <div style={{
        display: window.innerWidth <= 768 && videoUrl ? 'none' : 'flex', // Hide on mobile when video is uploaded
        gap: '12px',
        justifyContent: 'center',
        position: window.innerWidth <= 768 ? 'fixed' : 'static',
        bottom: window.innerWidth <= 768 ? '20px' : 'auto',
        left: window.innerWidth <= 768 ? '20px' : 'auto',
        right: window.innerWidth <= 768 ? '20px' : 'auto',
        zIndex: window.innerWidth <= 768 ? 1000 : 'auto',
        backgroundColor: 'transparent',
        padding: window.innerWidth <= 768 ? '16px' : '0',
        borderRadius: window.innerWidth <= 768 ? '12px' : '0'
      }}>
        <input
          ref={fileInputRef}
          type="file"
          accept="video/mp4,video/mov,video/webm"
          onChange={handleFileSelect}
          style={{ display: 'none' }}
        />
        





      </div>



      {/* Product Info Drawer */}
      <ProductInfoDrawer
        isOpen={showProductDrawer}
        onClose={() => setShowProductDrawer(false)}
        productInfo={productInfo}
        onUpdateProductInfo={updateProductInfo}
        taxonomyData={taxonomyData}
        variants={variants}
        setVariants={setVariants}
        videoUrl={videoUrl}
      />
    </div>
  );
} 